import { useEffect, useState } from 'react';

// =====================================================================================
// API access guard — the web client's half of the API's request firewall.
//
// Two refusals happen BEFORE any business code runs, so nothing was saved and a manual retry is
// always safe:
//   429 { code: "RATE_LIMIT_EXCEEDED", message, retryAfterSeconds, requestId } + header Retry-After
//   403 { code: "IP_BLOCKED", message, requestId }  (a global block, or the company's own block)
//
// - apiFetch (auth/Auth.tsx) throws `buildApiError(...)` for every non-2xx answer. The message is
//   still exactly "Error NNN: <message>" (about 16 screens parse it); the error also carries status,
//   code, retryAfterSeconds, requestId and the parsed body (`ApiError`).
// - Every failed answer goes through `notifyApiError`, which reacts ONLY to the two refusals above
//   and keeps one shared state, rendered by src/components/ApiAccessNotice: a "Too Many Requests"
//   notice with a live countdown and an "Access Denied" dialog — one per episode, however many
//   requests fail meanwhile. A screen with its own wording calls `explainApiAccessFailure`.
// - An IP block never signs anyone out and is never retried automatically; a rate limit is retried
//   automatically only after the server's wait (`rateLimitWaitMs`, `hubRetryDelayMs`).
// - Feature-local 429s are NOT this condition: device-settings report (code RATE_LIMITED), AI items /
//   voice (code rate_limited), AI chat ({ error }). Only code RATE_LIMIT_EXCEEDED is — or a 429 whose
//   body is not a JSON object at all (a proxy / IIS limiter in front of the API).
// - Every request names the app with `X-Client-App: web` (CLIENT_APP_HEADERS) for the API's traffic
//   analytics.
// =====================================================================================

export const CLIENT_APP_HEADER = 'X-Client-App';
export const CLIENT_APP_NAME = 'web';
/** Spread into a request's headers: `{ ...CLIENT_APP_HEADERS, Authorization: … }`. */
export const CLIENT_APP_HEADERS: Readonly<Record<string, string>> = Object.freeze({
  [CLIENT_APP_HEADER]: CLIENT_APP_NAME
});

export const RATE_LIMIT_CODE = 'RATE_LIMIT_EXCEEDED';
export const IP_BLOCKED_CODE = 'IP_BLOCKED';
/** 400 from an OTP / reset-code check: too many wrong codes — the code was invalidated. */
export const OTP_ATTEMPTS_EXCEEDED_CODE = 'OTP_ATTEMPTS_EXCEEDED';

/** Wait assumed when a 429 names none (a proxy limiter without Retry-After). */
export const DEFAULT_RATE_LIMIT_SECONDS = 10;
/** Longest wait honoured; a longer Retry-After is clamped to it. */
export const MAX_RATE_LIMIT_SECONDS = 3600;
/** Automatic work (hub restarts, scan sync) waits this long between attempts while IP-blocked. */
export const IP_BLOCKED_RETRY_MS = 60_000;
/** A hub negotiate refused with 429 and no readable wait. */
const HUB_RATE_LIMITED_RETRY_MS = 60_000;
/**
 * A 2xx this long after the last IP_BLOCKED refusal means the block is over. A shorter gap is a
 * request that was already past the firewall when the block began — it proves nothing.
 */
const IP_BLOCK_SETTLE_MS = 10_000;

/** What apiFetch throws for a non-2xx answer: a plain Error with these properties attached. */
export interface ApiError extends Error {
  status: number;
  /** The body's `code` (RATE_LIMIT_EXCEEDED, IP_BLOCKED, OTP_ATTEMPTS_EXCEEDED, …) or ''. */
  code: string;
  /** The wait the answer asked for (body retryAfterSeconds / Retry-After), in whole seconds; null = none. */
  retryAfterSeconds: number | null;
  /** The body's requestId (or the X-Request-Id header) — quoted to support. */
  requestId: string | null;
  /** The parsed JSON body, or the raw text when it was not JSON. */
  body: unknown;
}

/** A failure's own wording for the shared notice (see `explainApiAccessFailure`). */
export interface AccessFailureDetail {
  /** A line under the "Too Many Requests" notice. */
  rateLimited?: string;
  /** A line in the "Access Denied" dialog. */
  ipBlocked?: string;
}

/** What a till says when a payment is refused this way: nothing was saved, the cart stays on screen. */
export const PAYMENT_NOT_SAVED_DETAIL: AccessFailureDetail = {
  rateLimited: 'The bill was not saved — wait for the countdown, then press Pay again. Your cart is kept.',
  ipBlocked: 'The bill was not saved. Your cart is kept.'
};

interface HeaderSource {
  get(name: string): string | null;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/** A body field in camel or Pascal case. */
const field = (o: unknown, name: string): unknown => {
  if (!isPlainObject(o)) return undefined;
  if (o[name] !== undefined) return o[name];
  return o[name.charAt(0).toUpperCase() + name.slice(1)];
};

const clampSeconds = (seconds: number): number =>
  Math.min(Math.max(Math.ceil(seconds), 1), MAX_RATE_LIMIT_SECONDS);

/**
 * The wait a refusal asked for, in whole seconds (1…3600): the body's `retryAfterSeconds`, else the
 * Retry-After header (delta-seconds or an HTTP date). null when neither is usable.
 */
export function parseRetryAfterSeconds(
  header: string | null | undefined,
  body?: unknown,
  nowMs: number = Date.now()
): number | null {
  const fromBody = Number(field(body, 'retryAfterSeconds') ?? field(body, 'retryAfter'));
  if (Number.isFinite(fromBody) && fromBody > 0) return clampSeconds(fromBody);
  const raw = typeof header === 'string' ? header.trim() : '';
  if (!raw) return null;
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const seconds = Number(raw);
    return seconds > 0 ? clampSeconds(seconds) : null;
  }
  const at = Date.parse(raw);
  return Number.isFinite(at) && at > nowMs ? clampSeconds((at - nowMs) / 1000) : null;
}

/**
 * A body that is a web server's own HTML page (IIS "403 - Forbidden: Access is denied.", a proxy's
 * 502 …), not one of the API's JSON answers: the request was turned away before it reached the API.
 */
export function isHtmlPage(text: unknown): boolean {
  if (typeof text !== 'string') return false;
  const head = text.trimStart().slice(0, 200).toLowerCase();
  return head.startsWith('<') && ['<html', '<!doctype', '<head', '<body'].some((tag) => head.includes(tag));
}

/** The short words for such a page, by status — shown instead of the page (same wording as the mobile app). */
export function serverPageMessage(status: number): string {
  switch (status) {
    case 401:
      return 'Session expired. Please sign in again.';
    case 403:
      return 'Too many requests or access blocked (403). Try again in a moment.';
    case 404:
      return 'Not found on the server (404).';
    case 408:
    case 504:
      return 'The server took too long to answer. Try again.';
    case 429:
      return 'Too many requests. Try again in a moment.';
    case 502:
    case 503:
      return 'Server unavailable right now. Try again in a moment.';
    default:
      return `Server error (${status}). Try again in a moment.`;
  }
}

/** A failed answer's text as a screen may show it: a web server's HTML page becomes its short words; anything else as given. */
export function readableErrorText(status: number, text: string): string {
  return status >= 400 && isHtmlPage(text) ? serverPageMessage(status) : text;
}

/** A failed answer's body: the parsed JSON when it is JSON, else the raw text. */
export function parseErrorBody(text: string): unknown {
  if (!text) return text;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** The thrown error: `message` exactly as given; status / code / wait / request id / body attached. */
export function createApiError(
  status: number,
  message: string,
  body: unknown,
  headers?: HeaderSource | null
): ApiError {
  // A plain Error with properties, not a subclass: a transpiled Error subclass breaks instanceof.
  const error = new Error(message) as ApiError;
  const code = field(body, 'code');
  const requestId = field(body, 'requestId') ?? (headers ? headers.get('X-Request-Id') : null);
  error.status = status;
  error.code = typeof code === 'string' ? code : '';
  error.retryAfterSeconds = parseRetryAfterSeconds(headers ? headers.get('Retry-After') : null, body);
  error.requestId = typeof requestId === 'string' && requestId ? requestId : null;
  error.body = body;
  return error;
}

/**
 * The error apiFetch throws for a non-2xx answer whose body text was `errorText`. The message is the
 * historic one, unchanged: the body's `message` (+ " (details)"), else its `error`, else the raw text
 * or the status text — prefixed "Error NNN: ".
 */
export function buildApiError(
  status: number,
  statusText: string,
  errorText: string,
  headers?: HeaderSource | null
): ApiError {
  let errorMessage: unknown = errorText || statusText;
  let body: unknown = errorText;
  try {
    const errorJson = JSON.parse(errorText);
    body = errorJson;
    // Backend returns structured errors with 'error', 'message', and 'details'
    if (errorJson.message) {
      errorMessage = errorJson.message;
      if (errorJson.details) errorMessage = `${errorMessage} (${errorJson.details})`;
    } else if (errorJson.error) {
      errorMessage = errorJson.error;
    }
  } catch {
    // Not JSON (or JSON null): the text as is — but a web server's HTML page only as its short words
    // ("Too many requests or access blocked (403)…"), never the page (it filled whole screens).
    errorMessage = readableErrorText(status, errorText) || statusText;
    body = readableErrorText(status, errorText);
  }
  return createApiError(status, `Error ${status}: ${errorMessage}`, body, headers);
}

// ── Reading a failure ───────────────────────────────────────────────────────────────────

/** HTTP status of an API failure: its `status`, else the "Error NNN" message prefix; 0 = no HTTP answer. */
export function apiErrorStatus(e: unknown): number {
  if (!e || typeof e !== 'object') return 0;
  const own = (e as { status?: unknown }).status;
  if (typeof own === 'number' && own > 0) return own;
  const match = /^Error (\d{3})/.exec(String((e as { message?: unknown }).message ?? ''));
  return match ? Number(match[1]) : 0;
}

/** The body's `code` of an API failure ('' when none). */
export function apiErrorCode(e: unknown): string {
  if (!e || typeof e !== 'object') return '';
  const own = (e as { code?: unknown }).code;
  if (typeof own === 'string' && own) return own;
  const fromBody = field((e as { body?: unknown }).body, 'code');
  return typeof fromBody === 'string' ? fromBody : '';
}

/** The server's own sentence, without apiFetch's "Error NNN: " prefix. */
export function apiErrorMessage(e: unknown, fallback = ''): string {
  const raw = String((e as { message?: unknown } | null | undefined)?.message ?? '');
  return raw.replace(/^Error \d{3}:\s*/, '').trim() || fallback;
}

/** The refused request's id (body requestId / X-Request-Id), or null. */
export function apiErrorRequestId(e: unknown): string | null {
  if (!e || typeof e !== 'object') return null;
  const own = (e as { requestId?: unknown }).requestId;
  if (typeof own === 'string' && own) return own;
  const fromBody = field((e as { body?: unknown }).body, 'requestId');
  return typeof fromBody === 'string' && fromBody ? fromBody : null;
}

/** The wait a failure asked for, in whole seconds, or null. */
export function apiErrorRetryAfterSeconds(e: unknown): number | null {
  if (!e || typeof e !== 'object') return null;
  const own = (e as { retryAfterSeconds?: unknown }).retryAfterSeconds;
  if (typeof own === 'number' && Number.isFinite(own) && own > 0) return clampSeconds(own);
  return parseRetryAfterSeconds(null, (e as { body?: unknown }).body);
}

/**
 * The API's own rate limiter refused the request (429 RATE_LIMIT_EXCEEDED) — or something in front
 * of it did (a 429 whose body is not a JSON object). A feature's own 429 (code RATE_LIMITED /
 * rate_limited, the AI chat's { error }) is NOT this: its screen handles it.
 */
export function isRateLimitError(e: unknown): boolean {
  if (apiErrorStatus(e) !== 429) return false;
  const code = apiErrorCode(e);
  if (code) return code === RATE_LIMIT_CODE;
  // No code at all: only a body that is not a JSON object (empty, text, HTML) — and only when the
  // error carries its body (an error built elsewhere proves nothing).
  return !!e && typeof e === 'object' && 'body' in e && !isPlainObject((e as { body?: unknown }).body);
}

/** The API refuses this network (403 IP_BLOCKED). Never a reason to sign out; never retried automatically. */
export function isIpBlockedError(e: unknown): boolean {
  return apiErrorStatus(e) === 403 && apiErrorCode(e) === IP_BLOCKED_CODE;
}

/** A rate limit or an IP block (nothing was saved). */
export const isApiAccessError = (e: unknown): boolean => isRateLimitError(e) || isIpBlockedError(e);

// ── The shared state ────────────────────────────────────────────────────────────────────

export interface ApiAccessState {
  /** Epoch ms the current rate-limit window ends; 0 = not rate-limited. */
  rateLimitedUntil: number;
  /** The server named the wait (the notice counts down); false = DEFAULT_RATE_LIMIT_SECONDS assumed. */
  rateLimitDelayKnown: boolean;
  /** The "Too Many Requests" notice is showing (its close button hides it for the episode). */
  rateLimitNoticeOpen: boolean;
  /** A screen's own line under that notice. */
  rateLimitDetail: string | null;
  /** The API refuses this network. */
  ipBlocked: boolean;
  /** The "Access Denied" dialog is showing. */
  ipBlockedNoticeOpen: boolean;
  /** A screen's own line in that dialog. */
  ipBlockedDetail: string | null;
  /** The last refused request's id. */
  requestId: string | null;
}

const INITIAL_STATE: ApiAccessState = {
  rateLimitedUntil: 0,
  rateLimitDelayKnown: false,
  rateLimitNoticeOpen: false,
  rateLimitDetail: null,
  ipBlocked: false,
  ipBlockedNoticeOpen: false,
  ipBlockedDetail: null,
  requestId: null
};

let state: ApiAccessState = INITIAL_STATE;
const listeners = new Set<() => void>();
let rateLimitTimer: ReturnType<typeof setTimeout> | null = null;
let ipLastRefusalAt = 0;
/** This episode's refusal texts: a page toast repeating one only duplicates the notice. */
let rateLimitTexts: string[] = [];
let ipBlockedTexts: string[] = [];
/** Errors already counted (one error can arrive from apiFetch AND from its screen). */
let counted = new WeakSet<object>();

function setState(patch: Partial<ApiAccessState>): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* a listener never breaks the API layer */
    }
  });
}

export const getApiAccessState = (): ApiAccessState => state;

export function subscribeApiAccess(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** What is left of the current rate-limit window, in ms (0 = none). */
export function rateLimitRemainingMs(now: number = Date.now()): number {
  return Math.max(0, state.rateLimitedUntil - now);
}

const withText = (list: string[], text: unknown): string[] => {
  const t = typeof text === 'string' ? text.trim() : '';
  if (t.length < 8 || list.includes(t)) return list;
  return [...list, t].slice(-10);
};

/** The thrown message and the server's own sentence of a refusal. */
const refusalTexts = (list: string[], e: unknown): string[] =>
  withText(withText(list, (e as { message?: unknown }).message), field((e as { body?: unknown }).body, 'message'));

function endRateLimit(): void {
  if (rateLimitTimer) clearTimeout(rateLimitTimer);
  rateLimitTimer = null;
  rateLimitTexts = [];
  setState({ rateLimitedUntil: 0, rateLimitDelayKnown: false, rateLimitNoticeOpen: false, rateLimitDetail: null });
}

function scheduleRateLimitEnd(): void {
  if (rateLimitTimer) clearTimeout(rateLimitTimer);
  rateLimitTimer = setTimeout(() => {
    rateLimitTimer = null;
    if (state.rateLimitedUntil > Date.now()) scheduleRateLimitEnd();
    else endRateLimit();
  }, rateLimitRemainingMs() + 50);
}

/**
 * Every failed API answer comes through here (apiFetch, the raw-fetch clients). Only the two
 * refusals change anything; returns true when `e` is one of them.
 */
export function notifyApiError(e: unknown): boolean {
  const rateLimited = isRateLimitError(e);
  if (!rateLimited && !isIpBlockedError(e)) return false;
  const err = e as object;
  if (counted.has(err)) return true;
  counted.add(err);
  const now = Date.now();
  const requestId = apiErrorRequestId(e) ?? state.requestId;

  if (rateLimited) {
    const seconds = apiErrorRetryAfterSeconds(e);
    const active = state.rateLimitedUntil > now;
    rateLimitTexts = refusalTexts(active ? rateLimitTexts : [], e);
    setState({
      rateLimitedUntil: Math.max(active ? state.rateLimitedUntil : 0, now + (seconds ?? DEFAULT_RATE_LIMIT_SECONDS) * 1000),
      rateLimitDelayKnown: seconds !== null || (active && state.rateLimitDelayKnown),
      // A new episode opens the notice; within one, a notice the user closed stays closed.
      rateLimitNoticeOpen: active ? state.rateLimitNoticeOpen : true,
      rateLimitDetail: active ? state.rateLimitDetail : null,
      requestId
    });
    scheduleRateLimitEnd();
    return true;
  }

  const newEpisode = !state.ipBlocked;
  ipLastRefusalAt = now;
  ipBlockedTexts = refusalTexts(newEpisode ? [] : ipBlockedTexts, e);
  setState({
    ipBlocked: true,
    // A new episode opens the dialog; a dialog the user closed stays closed for background refusals
    // (a screen's own action reopens it through explainApiAccessFailure).
    ipBlockedNoticeOpen: newEpisode ? true : state.ipBlockedNoticeOpen,
    ipBlockedDetail: newEpisode ? null : state.ipBlockedDetail,
    requestId
  });
  return true;
}

/**
 * For a screen's own error handler (a payment, a sign-in): when `e` is a rate limit or an IP block,
 * (re)opens the shared notice with this screen's line and returns true — show nothing else, the
 * notice says it all (the wait included). False for any other error, and for a rate limit whose
 * window already ended: the screen shows its usual message.
 */
export function explainApiAccessFailure(e: unknown, detail?: AccessFailureDetail): boolean {
  if (!notifyApiError(e)) return false;
  if (isRateLimitError(e)) {
    if (state.rateLimitedUntil <= Date.now()) return false;
    setState({ rateLimitNoticeOpen: true, rateLimitDetail: detail?.rateLimited ?? null });
    return true;
  }
  setState({ ipBlocked: true, ipBlockedNoticeOpen: true, ipBlockedDetail: detail?.ipBlocked ?? null });
  return true;
}

/** The notice's close button: hidden for the rest of this episode (a new one opens it again). */
export function dismissRateLimitNotice(): void {
  if (state.rateLimitNoticeOpen) setState({ rateLimitNoticeOpen: false });
}

/** The dialog's Close: hidden until a screen's own action is refused again (or a new episode). */
export function dismissIpBlockedNotice(): void {
  if (state.ipBlockedNoticeOpen) setState({ ipBlockedNoticeOpen: false });
}

/** The API answered this browser again: the IP block is over. */
export function clearIpBlocked(): void {
  ipBlockedTexts = [];
  if (state.ipBlocked || state.ipBlockedNoticeOpen || state.ipBlockedDetail) {
    setState({ ipBlocked: false, ipBlockedNoticeOpen: false, ipBlockedDetail: null });
  }
}

/** apiFetch got a 2xx: an IP block noted a while ago (not a request racing the block itself) is over. */
export function noteApiSuccess(now: number = Date.now()): void {
  if (state.ipBlocked && now - ipLastRefusalAt >= IP_BLOCK_SETTLE_MS) clearIpBlocked();
}

/** Back to the initial state (tests). */
export function resetApiAccessState(): void {
  if (rateLimitTimer) clearTimeout(rateLimitTimer);
  rateLimitTimer = null;
  rateLimitTexts = [];
  ipBlockedTexts = [];
  ipLastRefusalAt = 0;
  counted = new WeakSet<object>();
  setState(INITIAL_STATE);
}

/**
 * A page toast that only repeats a refusal the open notice already explains (ApiAccessNotice drops
 * those: one alert per episode). Only while that notice shows, and only for the refusal's own text.
 */
export function isDuplicateOfAccessNotice(content: unknown, now: number = Date.now()): boolean {
  if (typeof content !== 'string' || !content) return false;
  const repeats = (texts: string[]): boolean => texts.some((t) => content.includes(t));
  if (state.rateLimitNoticeOpen && state.rateLimitedUntil > now && repeats(rateLimitTexts)) return true;
  return state.ipBlocked && state.ipBlockedNoticeOpen && repeats(ipBlockedTexts);
}

// ── Automatic retries ───────────────────────────────────────────────────────────────────

/**
 * How long automatic work waits after a rate-limit refusal: the server's wait, else what is left of
 * the current window (at least the default).
 */
export function rateLimitWaitMs(e: unknown, now: number = Date.now()): number {
  const seconds = apiErrorRetryAfterSeconds(e);
  if (seconds !== null) return seconds * 1000;
  return Math.max(rateLimitRemainingMs(now), DEFAULT_RATE_LIMIT_SECONDS * 1000);
}

/**
 * Status, code and wait of a SignalR start / reconnect failure. A negotiate failure arrives wrapped
 * ("Failed to complete negotiation with the server: Error: <body>: Status code '429'"), so without a
 * `statusCode` the status and the JSON body are read back from the message.
 */
export function hubFailureInfo(error: unknown): { status: number; code: string; retryAfterSeconds: number | null } {
  const message = String((error as { message?: unknown } | null | undefined)?.message ?? '');
  const own = Number((error as { statusCode?: unknown } | null | undefined)?.statusCode);
  const match = /Status code '(\d{3})'/.exec(message);
  let status = 0;
  if (Number.isFinite(own) && own > 0) status = own;
  else if (match) status = Number(match[1]);
  let body: unknown = null;
  const start = message.indexOf('{');
  const end = message.lastIndexOf('}');
  if (start >= 0 && end > start) body = parseErrorBody(message.slice(start, end + 1));
  const code = field(body, 'code');
  return { status, code: typeof code === 'string' ? code : '', retryAfterSeconds: parseRetryAfterSeconds(null, body) };
}

/**
 * Pause before the next hub (re)start: `normalMs` normally; IP_BLOCKED_RETRY_MS while this browser is
 * IP-blocked or after a 403; after a 429 the server's wait (else 60 s); inside a rate-limit window at
 * least what is left of it. Never a 5-second loop against a refusal.
 */
export function hubRetryDelayMs(error: unknown, normalMs: number, now: number = Date.now()): number {
  const info = hubFailureInfo(error);
  if (state.ipBlocked || info.code === IP_BLOCKED_CODE || info.status === 403) {
    return Math.max(IP_BLOCKED_RETRY_MS, normalMs);
  }
  const windowMs = rateLimitRemainingMs(now);
  if (info.status === 429) {
    const askedMs = info.retryAfterSeconds !== null ? info.retryAfterSeconds * 1000 : HUB_RATE_LIMITED_RETRY_MS;
    return Math.max(askedMs, windowMs, normalMs);
  }
  return Math.max(windowMs, normalMs);
}

/** "45s", "4:05". */
export function formatWait(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ── React ───────────────────────────────────────────────────────────────────────────────

/** The shared state; re-renders on every change. */
export function useApiAccessState(): ApiAccessState {
  const [snapshot, setSnapshot] = useState<ApiAccessState>(getApiAccessState);
  useEffect(() => {
    const sync = () => setSnapshot(getApiAccessState());
    sync();
    return subscribeApiAccess(sync);
  }, []);
  return snapshot;
}

/** Seconds left in the rate-limit window (0 = none), ticking while it runs. */
export function useRateLimitCountdown(): number {
  const { rateLimitedUntil } = useApiAccessState();
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (rateLimitedUntil <= Date.now()) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, [rateLimitedUntil]);
  return rateLimitedUntil > now ? Math.ceil((rateLimitedUntil - now) / 1000) : 0;
}
